import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";

/**
 * Trail shown at the top of hub pages, e.g. Home > Fee Management > Fee Reports.
 * Solves the "extra click" cost of hub navigation: users land one level deeper
 * than the old flat sidebar, so they need a way back up.
 *
 * @param {Array} items - [{ label, url }]. `url` is optional on the LAST item
 *                         (current page — rendered as plain text, not a link).
 *                         `url` should already include any /staff-panel prefix
 *                         the caller wants — Breadcrumb does not transform paths,
 *                         unlike HubGrid/HubSection (keeps this component dumb).
 * @param {string} [homeUrl] - defaults to "/school-home"; pass "/staff-home" for
 *                              staff portal pages.
 */
const Breadcrumb = ({ items = [], homeUrl = "/school-home" }) => {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1 text-sm mb-6">
      <Link
        to={homeUrl}
        className="flex items-center text-gray-400 dark:text-neutral-500 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded"
        aria-label="Home"
      >
        <Home className="w-4 h-4" />
      </Link>

      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        return (
          <span key={`${item.label}-${index}`} className="flex items-center gap-1">
            <ChevronRight className="w-3.5 h-3.5 text-gray-300" aria-hidden="true" />
            {isLast || !item.url ? (
              <span className="text-gray-700 dark:text-neutral-200 font-medium" aria-current={isLast ? "page" : undefined}>
                {item.label}
              </span>
            ) : (
              <Link
                to={item.url}
                className="text-gray-400 dark:text-neutral-500 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded"
              >
                {item.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
};

export default Breadcrumb;
